import { ReactNode, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Row, SortingState } from '@tanstack/react-table';

import Page from 'components/layouts/page/Page';
import ControlledTable from 'components/shared/table/ControlledTable';
import usePagination from 'hooks/usePagination';
import useWorkflows from 'hooks/useWorkflows';
import Workflow from 'models/Workflow';
import { Body, H2 } from 'styles/typography';

import { REFERRAL_INTAKE_API_NAME } from './constants';
import inboundReferralsTableColumns from './inboundReferralsTableColumns';

interface Props {
  title: string;
  // The workflow type this inbox shows (definition apiName).
  apiName: string;
  // Row-click destination base; rows navigate to `${basePath}/${id}`.
  basePath: string;
  // Optional header control (e.g. the New referral button).
  headerAction?: ReactNode;
}

// The one inbox implementation, shared by the per-type list pages. The index returns
// every run the user can see; each page filters to its own workflow type.
export default function WorkflowListPage({ title, apiName, basePath, headerAction }: Props) {
  const navigate = useNavigate();
  const { workflows, isLoading, isError } = useWorkflows();

  // A row without a definition predates the typed index payload — those are all
  // referral intake, so they stay in that inbox rather than vanishing.
  // Client-side type filtering is safe only while the index is unpaginated; if it
  // becomes server-paginated, filtering after fetch would desync page counts/totals —
  // move the apiName filter into the index query then.
  const rows = useMemo(
    () =>
      workflows.filter((w) =>
        w.definition.apiName ? w.definition.apiName === apiName : apiName === REFERRAL_INTAKE_API_NAME
      ),
    [workflows, apiName]
  );

  // Phase 0 index is unpaginated/unsorted; drive the standard table single-page so
  // it matches the rest of the app (sorting is disabled per-column).
  const pagination = usePagination();
  const [sort, setSort] = useState<SortingState>([]);

  const columns = useMemo(() => inboundReferralsTableColumns(), []);

  return (
    <Page>
      <Page.Header>
        <HeaderInner>
          <H2>{title}</H2>
          {headerAction}
        </HeaderInner>
      </Page.Header>
      <Page.Content>
        {isError ? (
          <Body>We couldn’t load referrals.</Body>
        ) : (
          <ControlledTable<Workflow>
            data={rows}
            columns={columns}
            loading={isLoading}
            pageCount={1}
            pagination={pagination}
            sorting={{ sortBy: sort, setSortBy: setSort }}
            onRowClick={(row: Row<Workflow>) => navigate(`${basePath}/${row.original.id}`)}
          />
        )}
      </Page.Content>
    </Page>
  );
}

const HeaderInner = styled.div`
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;
